import { z } from 'zod';

import { DayPortion } from '../types/time-off-request';

/** A HOURLY day can't exceed a standard working day (8h = 480 min). */
export const MAX_HOURLY_MINUTES = 8 * 60;

/**
 * Shared per-day sub-schema for the request form, reused by both the create
 * (validated) and details (hydrated) schemas. `dayPartNumber` is stored as a
 * string so it binds directly to the `SingleSelect` (string-valued); it is
 * coerced to a number when building the request payload.
 */
export const timeOffRequestDaySchema = z
  .object({
    date: z.string(),
    isWorkingDay: z.boolean(),
    portion: z.nativeEnum(DayPortion),
    dayPartNumber: z.string(),
    minutes: z.number().optional(),
  })
  .superRefine((day, ctx) => {
    if (!day.isWorkingDay) return;

    if (day.portion === DayPortion.Half || day.portion === DayPortion.Quarter) {
      const expected = day.portion === DayPortion.Half ? [1, 2] : [1, 2, 3, 4];
      if (!expected.includes(Number(day.dayPartNumber))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dayPartNumber'] });
      }
    }

    if (
      day.portion === DayPortion.Hourly &&
      !(day.minutes && day.minutes > 0 && day.minutes <= MAX_HOURLY_MINUTES)
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minutes'] });
    }
  });

export type TimeOffRequestDayFormValues = z.infer<typeof timeOffRequestDaySchema>;

/** Shared approver sub-schema (picked approver objects; ids derived at submit). */
export const timeOffRequestApproverSchema = z.object({
  id: z.number(),
  name: z.string(),
  profileImageUrl: z.string().nullable().optional(),
  /** True when this approver came from the suggested-approvers prefill. */
  isSuggested: z.boolean().optional(),
});

export type TimeOffRequestApproverFormValue = z.infer<typeof timeOffRequestApproverSchema>;

/** Shared date-range sub-schema (`to` may be null while a range is being picked). */
export const timeOffRequestDateRangeSchema = z.object({
  from: z.date(),
  to: z.date().nullable().optional(),
});

export type TimeOffRequestDateRangeValue = z.infer<typeof timeOffRequestDateRangeSchema>;
